/* eslint-disable @typescript-eslint/no-redeclare */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
// Types
import { APIResponseProps } from 'types/global';
import { ProductionEventProps, OrderLinesProps } from 'types/productionEvent';
// Auth Context
import { useAuthState } from 'context/AuthContext';
// Services
import { getItemDescriptionV1Data } from 'services/itemV1/item';
// Utils
import { useSetState, validArray, getRoleName } from 'utils';
import { checkActiveChildResendEvent, getEventCellData, setActionFill, setBadgeType } from 'utils/events';
// Assets
import Icon from 'assets/Icon';
// Elements
import Badge from 'elements/Badge';
import Button from 'elements/Button';
import ListCell from 'elements/ListCell';
// Components
import SubEvent from './SubEvent';
// Styles
import styles from './style/event.module.css';
// Config
import { SupplierId } from '../config';

interface EventProps {
  actionActive: boolean,
  event: ProductionEventProps,
  toggleAction: Function,
  activeEvent: ProductionEventProps,
}

function Event({
  actionActive,
  event,
  toggleAction,
  activeEvent,
}:EventProps) {
  // Ref
  const resolvePopover = useRef<HTMLLIElement>(null);
  // State
  const defaultState = useMemo(() => ({
    expandEvent: false,
    popover: false,
    errorMessage: '',
    resolutionMessage: '',
    childArray: [...event.orderLines],
  }), [event.orderLines]);
  const { defaultOrganisation: { roles, orgRef } } = useAuthState();
  const [state, setState] = useSetState(defaultState);
  const eventComplete = event.orderInfo.processingStatus.toUpperCase() === 'SUCCESS' || event.orderInfo.processingStatus.toUpperCase() === 'RESOLVED';
  const expandable = event.orderLines && event.orderLines.length > 0;
  // User Roles
  const userRoles = validArray(roles) ? roles.map(getRoleName) : [];
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (e.target instanceof Element) {
      if (state.popover && resolvePopover.current && resolvePopover.current.contains(e.target)) {
        return;
      }
    }
    setState({ popover: false });
  }, [setState, state.popover]);

  function renderCellData(cell:{ label: string, value: string | number }) {
    return (
      <ListCell
        key={cell.label}
        label={cell.label}
        value={cell.value}
        responsive="1280"
      />
    );
  }

  function fetchWorldType(orderLine: OrderLinesProps) {
    for (const [key, items] of Object.entries(SupplierId)) {
      if (orgRef === key) {
        items.forEach((item: { supplierId: string, type: string }) => {
          if (orderLine.supplierId === item.supplierId) {
            orderLine.worldType = item.type;
          }
        });
      }      
    }
  }

  async function fetchItemDescription(packId : string, orderLine: OrderLinesProps) {
    let data = {};
    // Condition for Old and New World Supplier Id
    if (orderLine.worldType === 'old') {
      data = {
        SMS_ID: packId,
      }
    } else if (orderLine.worldType === 'new') {
      data = {
        RMS_ID: packId,
      }
    }
    const { response }: APIResponseProps = await getItemDescriptionV1Data({
      reqParams: data,
    });
    if (response) {
      state.childArray.map((item: OrderLinesProps) => {
        if( item.packId === packId){
          item.itemDescription = response.itemDescription;
          item.itemRef = response.itemRef;
        }
        return item;
      });
      setState({
        error: '',
      });
    } else {
      state.childArray.map((item: OrderLinesProps) => {
        if( item.packId === packId){
          item.itemDescription = '-';
          item.itemRef = '-';
        }
        return item;
      });
      setState({
        error: 'Item description not found',
      });   
    }
    setState({ expandEvent: !state.expandEvent });
  }
  
  function getItemDescription(orderLine : any[]) {
    if (!state.expandEvent) {
      orderLine.map((item) => {
        fetchWorldType(item);
        fetchItemDescription(item.packId, item);
        return item;
      });
      setState({ expandEvent: !state.expandEvent });
     } else {
      setState({
        expandEvent: !state.expandEvent,
      });
    }
  }

  useEffect(() => {
    if (state.popover) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, state.popover]);

  useEffect(() => {
    setState(defaultState);
  }, [event, defaultState, setState])

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.processingStatus}>
          <Badge
            badgeStyle={setBadgeType((event.orderInfo.processingStatus === 'ERROR' || event.orderInfo.processingStatus === 'LINES_IN_ERROR') ? 'FAILED' : event.orderInfo.processingStatus)}
            label={(event.orderInfo.processingStatus === 'ERROR' || event.orderInfo.processingStatus === 'LINES_IN_ERROR') ? 'FAILED' : event.orderInfo.processingStatus}
          />
        </div>
        <div className={styles.details}>
          <h3 className="subtitle1">
            {event.orderInfo.id}
          </h3>
          <p className="body2">{ moment(event.customer.shipTo.deliveryDate).format('DD MMM YYYY hh:mm') }</p>
        </div>
        <ul className={styles.actions}>
          <li>
            <Button
              btnSize="small"
              btnType="icon"
              disabled={!expandable}
              elevation="2-4"
              handler={() => getItemDescription(event.orderLines)}
            >
              <Icon
                icon={state.expandEvent ? 'ExpandLess' : 'ExpandMore'}
                fill={expandable ? 'black' : 'warm-grey-200'}
              />
            </Button>
          </li>
          <li>
            {(userRoles.includes('PLANNING') || userRoles.includes('PLANNING ADMIN') || userRoles.includes('WAREHOUSE')) && (
            <Button
              btnSize="small"
              btnStyle={actionActive ? 'success' : ''}
              btnType="iconBefore"
              disabled={(event.orderInfo.processingStatus === 'LINES_IN_ERROR' || event.orderInfo.targetERP === 'INDEX')}
              elevation="2-4"
              handler={() => toggleAction()}
            >
              <Icon
                icon="AddAction"
                fill={setActionFill((eventComplete || event.orderInfo.orderType === 'PRINT_BATCH_TICKET'), actionActive)}
              />
              Resend
            </Button>
            )}
          </li>
        </ul>
        <div className={styles.cells}>
          { getEventCellData(event).map(renderCellData)}
        </div>
      </div>
      <div className={styles.cells1}>
        {event.orderInfo.errors && event.orderInfo.errors.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="body2" key={index}><span>{item.errorMessage}</span> <strong className="subtitle2">&nbsp;&nbsp;{item.resolutionMessage}</strong></div>
        ))}
      </div>
      <div className={`${styles.expandedContent} ${state.expandEvent ? styles.expandedContent_active : ''}`}>
        <div className={styles.childGrid}>
          { state.childArray && state.childArray.map((item: any) =>
            <SubEvent
              event={event}
              key={item.salesOrderLineId}
              childEvent={item}
              actionActive={checkActiveChildResendEvent(activeEvent, item)}
              toggleAction={toggleAction}
            />)}
        </div>
      </div>
    </div>
  );
}

export default Event;
